/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { Platform, View } from "react-native";
import ButtonBase from "@material-ui/core/ButtonBase";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Fade from "@material-ui/core/Fade";
import Icon from "react-icons-kit";
import { chevronDown } from "react-icons-kit/fa/chevronDown";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { Text } from "UpduoLibrary";
import {
  colorWithLuminance,
  skaleBlack,
  skaleBlue,
  skaleWebTextGray,
  upduoLightestGray,
} from "config/UpduoColors";
import useOrgSelector from "./useOrgSelector";
import { UserType } from "graphql/public/generated_public";
import { GetElementType, RecursivePartial } from "config/UtilityTypes";

export type OnOrgSelectChangeCallback = (
  userId: string,
  action: () => void
) => void;

export interface OrgSelectorProps {
  currentOrgID: string;
  onChange: OnOrgSelectChangeCallback;
  optionList: Array<RecursivePartial<UserType>>;
}

function OrgSelector({
  currentOrgID,
  onChange,
  optionList,
  history,
}: // TODO: once we figure out react-router types, these any's and unknown's should be filled in
OrgSelectorProps & RouteComponentProps<any, any, unknown>) {
  const {
    isHovering,
    anchorEl,
    text,
    setIsHovering,
    _handleClick,
    _handleChange,
    _handleClose,
  } = useOrgSelector({
    currentOrgID,
    onChange,
    optionList,
  });

  const fontSize = 14;
  const textColor = isHovering
    ? colorWithLuminance(skaleBlue, -0.2)
    : skaleBlack;

  const newOption = (
    <MenuItem
      key={"join"}
      style={{
        color: skaleWebTextGray,
        fontFamily: "ObjectSans-Regular",
        fontSize: fontSize,
      }}
      onClick={() => {
        history.push("/join_organization");
        _handleClose();
      }}
    >
      {"+ Join New Community"}
    </MenuItem>
  );

  // Hack for "library" organizations:
  // Only Content page for non-Maintainance users, plus Members page and editing only for Maintenance users:
  const libraryOrganizationIds = new Set(
    process.env.REACT_APP_LIBRARY_ORGANIZATION_IDS?.split(",")
  );

  const validOptions: {
    id: UserType["id"];
    organization: Pick<NonNullable<UserType["organization"]>, "id" | "name">;
  }[] = optionList.filter(
    (member) =>
      member.id && member.organization?.id && member.organization?.name
  ) as any;

  const joinedOptions = validOptions.filter(
    (member) => !libraryOrganizationIds.has(member.organization.id)
  );

  const libraryOptions = validOptions.filter((member) =>
    libraryOrganizationIds.has(member.organization.id)
  );

  const getMenuDivider = (key: string) => (
    <View
      key={key}
      style={{
        height: 1,
        width: "100%",
        backgroundColor: upduoLightestGray,
        marginVertical: 5,
      }}
    />
  );

  const makeMenuItem = (member: GetElementType<typeof joinedOptions>) => (
    <MenuItem
      key={member.id}
      value={member.id}
      style={{
        color: skaleWebTextGray,
        fontFamily: "ObjectSans-Regular",
        fontSize: fontSize,
      }}
      onClick={() => {
        _handleChange(member.id, member.organization.name);
      }}
    >
      {member.organization.name}
    </MenuItem>
  );

  const generateMenuOptions = () => {
    let options = [newOption];
    if (joinedOptions.length > 0) {
      options = options
        .concat([getMenuDivider("joinedOptionsDivider")])
        .concat(joinedOptions.map(makeMenuItem));
    }
    if (libraryOptions.length > 0) {
      options = options
        .concat([getMenuDivider("libraryOptionsDivider")])
        .concat(libraryOptions.map(makeMenuItem));
    }
    return options;
  };

  return (
    <>
      <ButtonBase
        aria-controls={"fade-menu"}
        aria-haspopup={"true"}
        onClick={_handleClick}
        style={{
          color: textColor,
          flexDirection: "row",
          marginRight: 10,
          height: 80,
          paddingLeft: 10,
          paddingRight: 10,
        }}
        {...Platform.select({
          web: {
            onMouseEnter: () => {
              setIsHovering(true);
            },
            onMouseLeave: () => {
              setIsHovering(false);
            },
          },
        })}
      >
        <Text
          style={{
            color: textColor,
            fontWeight: "bold",
            fontSize: fontSize,
            marginRight: 5,
            marginBottom: 2,
          }}
        >
          {text}
        </Text>
        <Icon size={12} icon={chevronDown} style={{ marginBottom: 4 }} />
      </ButtonBase>
      <Menu
        id={"fade-menu"}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={_handleClose}
        TransitionComponent={Fade}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {generateMenuOptions()}
      </Menu>
    </>
  );
}

export default withRouter(OrgSelector);
